import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { PlaceOutlined } from '@mui/icons-material'
import { Button, Checkbox, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'

import ScreenContainer from '../../components/screen'
import { ActivationLocationState } from '../../providers/activation-provider'
import Api from '../../utils/api'
import { formatAddress } from '../../utils/helpers'

import useStyles from './index.styles'

const ParticipationAgreementMultiple = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles
  const { additionalFolders, savedSignature, fromDashboard, nodeTypeName, nodeId, folderId, meshifyId, sidewalkId } = (useLocation().state || {}) as {
    additionalFolders: IFolderType[]
    savedSignature: string
    fromDashboard: boolean
  } & ActivationLocationState
  const selectedFolderId = folderId

  const [loading, setLoading] = useState<boolean>(true)
  const [additionalFoldersMeta, setAdditionalFoldersMeta] = useState<IFolderType[]>([])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [checkedFolders, setCheckedFolders] = useState<number[]>([selectedFolderId])

  useEffect(() => {
    if (!selectedFolderId || !additionalFolders?.length || !savedSignature) {
      navigate('/', { replace: true }) // Redirect to a safe route if no folder is selected or required data is missing
    }
  }, [selectedFolderId, additionalFolders, savedSignature, navigate])

  const multipleFoldersData = useMemo(() => {
    const thisFolder = additionalFoldersMeta.find((f) => f.id === selectedFolderId)
    if (thisFolder)
      return [
        thisFolder,
        ...additionalFoldersMeta.filter((f) => f.id !== selectedFolderId), // rest of the folders under it
      ]
    return additionalFoldersMeta
  }, [additionalFoldersMeta, selectedFolderId])

  const fetchFolderData = useCallback(async () => {
    if (!selectedFolderId) return
    setLoading(true)

    // The participation agreement endpoint returns additionalFolders, but they lack address info, so we need to fetch the full folder data
    const allResults: IFolderType[] = []
    const limit = 50

    const fetchPage = async (offset: number): Promise<void> => {
      const requestBody = {
        filters: [
          {
            fieldName: 'id',
            operator: 'in',
            value: additionalFolders?.map((f: IFolderType) => f.id) || [],
          },
        ],
        first: limit,
        after: btoa(offset.toString()),
      }

      const foldersResponse = await Api.post('/api/folders/query', requestBody)

      if (foldersResponse.ok) {
        const results = foldersResponse.body.results ?? []
        allResults.push(...results)
        const hasNextPage = foldersResponse.body.hasNextPage ?? false

        if (hasNextPage) {
          await fetchPage(offset + limit)
        }
      } else {
        enqueueSnackbar('Failed to fetch folder information')
      }
    }

    await fetchPage(0)
    setAdditionalFoldersMeta(allResults)
    setLoading(false)
  }, [additionalFolders, enqueueSnackbar, selectedFolderId])

  useEffect(() => {
    fetchFolderData()
  }, [fetchFolderData])

  const onNonePress = () => {
    setCheckedFolders([selectedFolderId])
  }

  const onAllPress = () => {
    setCheckedFolders([selectedFolderId, ...additionalFoldersMeta.map((f) => f.id)])
  }

  const submitMultipleAgreement = async () => {
    setIsSubmitting(true)
    const dedupedCheckedFolders = [...new Set(checkedFolders)]
    const response = await Api.post(`/api/v2/protect/participation-agreement`, {
      folderIds: dedupedCheckedFolders,
      signedBy: savedSignature,
    })
    if (response.ok) {
      if (fromDashboard) {
        navigate(`/dashboard/${selectedFolderId}/devices`)
      } else {
        navigate('/meet-sensor', { state: { folderId: selectedFolderId, nodeId, nodeTypeName, meshifyId, sidewalkId } }) // Pass along necessary state for sensor activation
      }
    } else {
      const apiSignature = response.body.details?.[0]?.value?.signature
      if (apiSignature === 'protect_not_found_missing_attributes') enqueueSnackbar('Failed to update agreements. Participant folder attributes are missing.')
      else if (apiSignature === 'protect_not_found_agreement_not_found') enqueueSnackbar('Failed to update agreements. Participation Agreement is missing.')
      else if (apiSignature === 'protect_precondition_failed_client_company_path_missing')
        enqueueSnackbar('Failed to update agreements. ClientCompanyPath is missing.')
      else if (apiSignature === 'protect_precondition_failed_program_path_missing') enqueueSnackbar('Failed to update agreements. ProgramPath is missing.')
      else if (apiSignature === 'protect_permission_denied_not_assigned_folder') enqueueSnackbar('You do not have permission to make changes to this account.')
      else enqueueSnackbar('Failed to update agreements, please try again')
    }
    setIsSubmitting(false)
  }

  return (
    <ScreenContainer loading={loading} headerProps={{ title: 'Setup' }}>
      <Grid container={true} sx={classes.outerContainer}>
        <Typography variant="h6">Apply signature to all locations?</Typography>

        <Grid container={true} sx={classes.selectionRow}>
          <Typography variant="body2" color="textSecondary">
            Select
          </Typography>
          <Button variant="text" onClick={onAllPress} sx={classes.bulkCheckboxButton}>
            All
          </Button>
          <Typography variant="body2" color="textSecondary">
            /
          </Typography>
          <Button variant="text" onClick={onNonePress} sx={classes.bulkCheckboxButton}>
            None
          </Button>
        </Grid>

        <FormGroup>
          {multipleFoldersData.map((item) => {
            const { name: folderName, id: currFolderId } = item
            return (
              <FormControlLabel
                key={currFolderId}
                disabled={currFolderId === selectedFolderId}
                checked={checkedFolders.includes(currFolderId)}
                onChange={(_e, checked) => setCheckedFolders((prev) => (checked ? [...prev, currFolderId] : prev.filter((id) => id !== currFolderId)))}
                control={<Checkbox />}
                sx={classes.checkboxContainer}
                labelPlacement="start"
                label={
                  <Grid container={true} size={12} sx={classes.checkboxLabel}>
                    <Typography variant="subtitle1">{folderName}</Typography>
                    <Grid container={true} size={12} sx={classes.checkboxAddressRow} spacing={1}>
                      <Grid size={1}>
                        <PlaceOutlined sx={classes.locationIcon} />
                      </Grid>

                      <Grid size={11}>
                        <Typography variant="caption" component="p" className="fs-exclude" color="textSecondary">
                          {formatAddress(item.information?.address ?? {})}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                }
              />
            )
          })}
        </FormGroup>

        <Grid container={true} sx={[classes.footerRow, classes.footer]}>
          <Button variant="contained" onClick={submitMultipleAgreement} loading={isSubmitting} disabled={isSubmitting} sx={classes.submitButton}>
            Continue
          </Button>
        </Grid>
      </Grid>
    </ScreenContainer>
  )
}

export default ParticipationAgreementMultiple
